import { useState } from 'react';
import {
    Chip,
    Collapse,
    Grid,
    Typography,
    Box,
    IconButton,
    Button,
    Paper,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { useSelector } from 'react-redux';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import { useAddNewPartnerMutation } from '../../api/partnersApiSlice';
import { selectAllRegionNames } from '../../api/regionsApiSlice';
import UpdateButton from './adminUtils/updateButton';
import SearchBar from './adminUtils/SearchBar';
import PaginationInfo from './PaginationInfo';

function YhteistyotahotList({ yhttahot, handleDeleteYhttaho, handleUpdateYhttaho, fetchData, onSuccess, onError }) {
    const [selectedYhttaho, setSelectedYhttaho] = useState(null);
    const [newYhttahoData, setNewYhttahoData] = useState({ tyyppi: '', nimi: '', maakunta: '' });
    const [addYhttahoData, setAddYhttahoData] = useState({ tyyppi: '', nimi: '', maakunta: '' });
    const [showAddForm, setShowAddForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [addNewYhttaho, { isLoading }] = useAddNewPartnerMutation();
    const regions = useSelector(selectAllRegionNames);
    const tyypit = ['Seurakunta', 'Muu taho', 'Koulutuskeskus'];

    const [searchTerm, setSearchTerm] = useState('');

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
    };

    const filteredYhteistyotahot = Array.isArray(yhttahot)
        ? yhttahot.filter((yhttaho) =>
            yhttaho.nimi.toLowerCase().includes(searchTerm.toLowerCase())
        )
        : [];
    const totalPages = Math.ceil(filteredYhteistyotahot.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedYhttahot = filteredYhteistyotahot.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleUserClick = (yhttaho) => {
        setShowAddForm(false);
        if (selectedYhttaho?.id === yhttaho.id) {
            setSelectedYhttaho(null);
            setNewYhttahoData({ tyyppi: '', nimi: '', maakunta: '' });
        } else {
            setSelectedYhttaho(yhttaho);
            setNewYhttahoData(yhttaho);
        }
    };

    const handleAddClick = () => {
        setSelectedYhttaho(null);
        setNewYhttahoData({ tyyppi: '', nimi: '', maakunta: '' });
        setShowAddForm(!showAddForm);
    };

    const handleLisääYhttaho = async (yhttaho) => {
        try {
            const result = await addNewYhttaho(yhttaho);
            if (result.error) onError('Yhteistyötahon luominen epäonnistui');
            if (!result.error) onSuccess('Yhteistyötahon luominen onnistui.', 'yhteistyotahot', result.data.id);
            setAddYhttahoData({ tyyppi: '', nimi: '', maakunta: '' });
            setShowAddForm(false);
            fetchData();
        } catch (error) {
            console.error(error);
            onError('Yhteistyötahon luominen epäonnistui');
        }
    };

    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                {/* Header and Search */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Yhteistyötahot
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                </Grid>

                {/* Yhteistyotahot List */}
                {displayedYhttahot.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei yhteistyötahoja näytettäväksi.
                    </Typography>
                ) : (
                    displayedYhttahot.map((yhttaho) => {
                        const isSelected = selectedYhttaho?.id === yhttaho.id;
                        const summaryText = [yhttaho.tyyppi, yhttaho.maakunta].filter(Boolean).join(' | ');

                        return (
                            <Box key={yhttaho.id} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => handleUserClick(yhttaho)}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, fontWeight: 600 }}>
                                            {yhttaho.nimi}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {summaryText || 'Ei lisätietoja'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                            {yhttaho.tyyppi && <Chip size="small" label={yhttaho.tyyppi} />}
                                            {yhttaho.maakunta && <Chip size="small" variant="outlined" label={yhttaho.maakunta} />}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteYhttaho(event, yhttaho);
                                            if (selectedYhttaho?.id === yhttaho.id) {
                                                setSelectedYhttaho(null);
                                                setNewYhttahoData({ tyyppi: '', nimi: '', maakunta: '' });
                                            }
                                        }}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box sx={{ mt: 1, p: 2, border: '1px solid', borderColor: 'grey.200', borderRadius: 2, bgcolor: 'grey.50' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Muokkaa yhteistyötahoa
                                        </Typography>
                                        <Grid container spacing={2} alignItems="center">
                                            <Grid item xs={12} sm={8}>
                                                <FormTextField
                                                    fieldName="nimi"
                                                    label="Nimi"
                                                    multiline
                                                    fullWidth
                                                    value={newYhttahoData.nimi}
                                                    onChange={(event) =>
                                                        setNewYhttahoData((prev) => ({ ...prev, nimi: event.target.value }))
                                                    }
                                                    sx={{ mb: 2 }}
                                                />
                                                <FormControl sx={{ width: '100%', mb: 2 }}>
                                                    <InputLabel id="tyyppi-label">Tyyppi</InputLabel>
                                                    <Select
                                                        labelId="tyyppi-label"
                                                        id="tyyppi-select"
                                                        value={newYhttahoData.tyyppi}
                                                        label="Tyyppi"
                                                        onChange={(event) =>
                                                            setNewYhttahoData((prev) => ({ ...prev, tyyppi: event.target.value }))
                                                        }
                                                    >
                                                        {tyypit.map((tyyppi) => (
                                                            <MenuItem key={tyyppi} value={tyyppi}>
                                                                {tyyppi}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                                <FormControl sx={{ width: '100%', mb: 2 }}>
                                                    <InputLabel id="maakunta-label">Maakunta</InputLabel>
                                                    <Select
                                                        labelId="maakunta-label"
                                                        id="maakunta-select"
                                                        value={newYhttahoData.maakunta}
                                                        label="Maakunta"
                                                        onChange={(event) =>
                                                            setNewYhttahoData((prev) => ({ ...prev, maakunta: event.target.value }))
                                                        }
                                                    >
                                                        {regions.map((region) => (
                                                            <MenuItem key={region} value={region}>
                                                                {region}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <UpdateButton handleUpdate={handleUpdateYhttaho} data={newYhttahoData} />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                {/* Add Form */}
                <Box sx={{ mt: 3 }}>
                    <Button
                        variant="contained"
                        startIcon={showAddForm ? <CloseIcon /> : <AddIcon />}
                        onClick={handleAddClick}
                        sx={{
                            bgcolor: showAddForm ? 'grey.600' : 'taphaBlue.main',
                            '&:hover': { bgcolor: showAddForm ? 'grey.700' : 'taphaBlue.dark' },
                        }}
                    >
                        {showAddForm ? 'Sulje' : 'Lisää yhteistyötaho'}
                    </Button>
                    {showAddForm && (
                        <Box
                            sx={{
                                mt: 2,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                borderRadius: 1,
                                bgcolor: 'grey.50',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                Lisää uusi yhteistyötaho
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={8}>
                                    <FormTextField
                                        key="addYhttahoNimi"
                                        fieldName="nimi"
                                        label="Nimi"
                                        multiline
                                        fullWidth
                                        value={addYhttahoData.nimi}
                                        onChange={(event) =>
                                            setAddYhttahoData((prev) => ({ ...prev, nimi: event.target.value }))
                                        }
                                        sx={{ mb: 2 }}
                                    />
                                    <FormControl sx={{ width: '100%', mb: 2 }}>
                                        <InputLabel id="tyyppi-add-label">Tyyppi</InputLabel>
                                        <Select
                                            labelId="tyyppi-add-label"
                                            id="tyyppi-add-select"
                                            value={addYhttahoData.tyyppi}
                                            label="Tyyppi"
                                            onChange={(event) =>
                                                setAddYhttahoData((prev) => ({ ...prev, tyyppi: event.target.value }))
                                            }
                                        >
                                            {tyypit.map((tyyppi) => (
                                                <MenuItem key={tyyppi} value={tyyppi}>
                                                    {tyyppi}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl sx={{ width: '100%', mb: 2 }}>
                                        <InputLabel id="maakunta-add-label">Maakunta</InputLabel>
                                        <Select
                                            labelId="maakunta-add-label"
                                            id="maakunta-add-select"
                                            value={addYhttahoData.maakunta}
                                            label="Maakunta"
                                            onChange={(event) =>
                                                setAddYhttahoData((prev) => ({ ...prev, maakunta: event.target.value }))
                                            }
                                        >
                                            {regions.map((region) => (
                                                <MenuItem key={region} value={region}>
                                                    {region}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={isLoading ? null : <AddIcon />}
                                        onClick={() => handleLisääYhttaho(addYhttahoData)}
                                        disabled={isLoading}
                                        sx={{ bgcolor: 'taphaGreen.main', '&:hover': { bgcolor: 'taphaGreen.dark' } }}
                                    >
                                        {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Lisää'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Box>

                {/* Pagination */}
                {filteredYhteistyotahot.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredYhteistyotahot}
                            array={yhttahot}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default YhteistyotahotList;